import type { ConnectedUsefulness, Content, GameState, Problem } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

const REPONSES: readonly { valeur: ConnectedUsefulness; libelle: string }[] = [
  { valeur: 'oui', libelle: T.analyser.oui },
  { valeur: 'en_partie', libelle: T.analyser.enPartie },
  { valeur: 'non', libelle: T.analyser.non },
];

/**
 * Étape 3 — Analyser.
 *
 * Pour chaque problème repéré : un objet connecté peut-il aider ? La réponse
 * ouvre l'explication du contenu.
 *
 * Les problèmes dont le contenu dit « non » reçoivent une mise en avant : c'est
 * le moment que le plan appelle « Ah ! », celui où la classe découvre qu'un
 * capteur ne règle pas tout. Il est repéré par `objet_connecte_utile`, pas par
 * un identifiant écrit en dur — un autre problème « non » en bénéficierait aussi.
 */
export function EtapeAnalyser({
  content,
  partie,
  onAnalyser,
}: {
  content: Content;
  partie: GameState;
  onAnalyser: (problemId: string, reponse: ConnectedUsefulness) => void;
}) {
  const parId = new Map(content.problemes.map((p) => [p.id, p]));
  const decouverts = partie.discoveredProblems
    .map((id) => parId.get(id))
    .filter((p): p is Problem => p !== undefined);

  return (
    <ul className="space-y-3">
      {decouverts.map((probleme) => {
        const reponse = partie.connectedAnalysis[probleme.id];
        const momentCle = probleme.objet_connecte_utile === 'non';
        const repondu = reponse !== undefined;

        return (
          <li
            key={probleme.id}
            className={`rounded-2xl border-2 p-4 ${
              repondu && momentCle ? 'border-violet-400 bg-violet-50' : 'border-slate-200 bg-white'
            }`}
          >
            <h3 className="text-lg font-bold text-slate-900">{probleme.titre}</h3>
            <p className="mt-1 text-sm text-slate-600">{T.analyser.question}</p>

            <div className="mt-3 flex flex-wrap gap-2">
              {REPONSES.map((choix) => (
                <button
                  key={choix.valeur}
                  type="button"
                  aria-pressed={reponse === choix.valeur}
                  onClick={() => onAnalyser(probleme.id, choix.valeur)}
                  className={`min-h-touch flex-1 rounded-xl border-2 px-4 font-semibold ${
                    reponse === choix.valeur
                      ? 'border-sky-600 bg-sky-600 text-white'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {choix.libelle}
                </button>
              ))}
            </div>

            {repondu && (
              <div className="mt-3 rounded-xl bg-slate-50 p-3">
                {momentCle && (
                  <p className="text-sm font-bold uppercase tracking-wide text-violet-700">
                    <span aria-hidden="true">💡 </span>
                    {T.analyser.momentCle}
                  </p>
                )}
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {T.analyser.explication}
                </p>
                <p className="mt-1 text-slate-800">{probleme.explication}</p>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
